import { createContext, ReactNode, useEffect, useMemo, useState } from 'react';
import { ENGLISH, LANGUAGES } from '../constants';
import { TranslationContextType } from '../types/context';
import translationsASW from '../localization/translate.asw.json';
import { useUserState } from '../hooks/context/useUserState';

const importJson = (name) => import(`../localization/translate.${name}.json`);

const defaultvalues: TranslationContextType = {
  isProvided: false,
  language: ENGLISH,
  translationMap: null,
  isLoading: true,
};

export const TranslationContext =
  createContext<TranslationContextType>(defaultvalues);

const TranslationContextProvider = ({ children }: { children: ReactNode }) => {
  const [translationMap, setTranslationMap] = useState<Record<
    string,
    string
  > | null>(defaultvalues.translationMap);
  const [isLoading, setIsLoading] = useState(defaultvalues.isLoading);

  const {
    state: { language: userLanguage },
  } = useUserState();

  const availableLanguages = LANGUAGES;

  useEffect(() => {
    setIsLoading(true);
    let ignore = false;

    async function loadTranslations() {
      const translationsMapping = {};
      for (let i = 0; i < availableLanguages.length; i++) {
        let language;
        try {
          // try to get json file for language
          language = await importJson(availableLanguages[i].id);
        } catch (err) {
          try {
            language = await importJson(ENGLISH);
          } catch (e) {
            // if there is no english then we get translationsASW which always must be presented
            language = { default: translationsASW };
          }
        }
        translationsMapping[availableLanguages[i].id] = language.default;
      }
      if (!ignore) {
        setTranslationMap(translationsMapping);
      }
      setIsLoading(false);
    }

    loadTranslations();

    return () => {
      ignore = true;
    };
  }, []);

  const value = useMemo(
    () => ({
      isProvided: true,
      language: userLanguage,
      translationMap,
      isLoading,
    }),
    [userLanguage, translationMap, isLoading]
  );

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export default TranslationContextProvider;
